/**
 * Event Registration Service
 * 
 * Handles event registration operations with capacity management
 */

import { safeFrom, safeAuth, isSupabaseAvailable } from '@/lib/safe-supabase';
import { 
  ServiceResponse, 
  successResponse, 
  errorResponse, 
  handleServiceError,
  notConfiguredError
} from '@/lib/service-errors';
import { auditContent } from '@/lib/audit-logger';
import { 
  emailService,
  sendEventRegistrationConfirmation
} from './emailService';
import type { 
  RegistrationData, 
  EventRegistration, 
  RegistrationStatus 
} from '@/types/events';
import { eventsService } from './eventsService';

/**
 * Register for an event
 */
export const registerForEvent = async (
  eventId: number,
  data: RegistrationData
): Promise<ServiceResponse<EventRegistration>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    // Validate required fields
    if (!data.name || !data.email) {
      return errorResponse(handleServiceError(
        new Error('Name and email are required'),
        'eventRegistrationService.registerForEvent',
        { eventId, data }
      ));
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      return errorResponse(handleServiceError(
        new Error('Invalid email address'),
        'eventRegistrationService.registerForEvent',
        { eventId, data }
      ));
    }

    // Get event to check capacity
    const eventResult = await eventsService.getEvent(eventId);
    if (!eventResult.success || !eventResult.data) {
      return errorResponse(handleServiceError(
        new Error('Event not found'),
        'eventRegistrationService.registerForEvent',
        { eventId }
      ));
    }

    const event = eventResult.data;

    // Check if event is at capacity
    if (event.capacity > 0 && event.registered >= event.capacity) {
      return errorResponse(handleServiceError(
        new Error('Event is at full capacity'),
        'eventRegistrationService.registerForEvent',
        { eventId, capacity: event.capacity, registered: event.registered }
      ));
    }

    // Check for duplicate registration (unique constraint will also catch this)
    const { data: existing } = await safeFrom('event_registrations')
      .select('id')
      .eq('event_id', eventId)
      .eq('email', data.email.trim().toLowerCase())
      .maybeSingle();

    if (existing) {
      return errorResponse(handleServiceError(
        new Error('You are already registered for this event'),
        'eventRegistrationService.registerForEvent',
        { eventId, email: data.email }
      ));
    }

    // Create registration
    const { data: registration, error: insertError } = await safeFrom('event_registrations')
      .insert([{
        event_id: eventId,
        name: data.name.trim(),
        email: data.email.trim().toLowerCase(),
        phone: data.phone?.trim() || null,
        additional_info: data.additional_info?.trim() || null,
        status: 'registered' as RegistrationStatus,
      }])
      .select()
      .single();

    if (insertError) {
      // Check if it's a duplicate constraint violation
      if (insertError.code === '23505') {
        return errorResponse(handleServiceError(
          new Error('You are already registered for this event'),
          'eventRegistrationService.registerForEvent',
          { eventId, email: data.email }
        ));
      }
      throw insertError;
    }

    const created = registration as EventRegistration;

    // Send confirmation email (non-blocking)
    sendEventRegistrationConfirmation({
      name: created.name,
      email: created.email,
      eventTitle: event.title,
      eventDate: event.date,
      eventTime: event.time,
      eventLocation: event.location,
    }, import.meta.env.VITE_ORG_NAME || 'Our Organization').catch(error => {
      console.error('[eventRegistrationService] Failed to send confirmation email:', error);
    });

    // Audit log
    await auditContent.create('event_registration', created.id.toString(), {
      event_id: eventId,
      email: created.email,
    });

    return successResponse(created);
  } catch (error) {
    console.error('[eventRegistrationService] Error registering for event:', error);
    return errorResponse(handleServiceError(error, 'eventRegistrationService.registerForEvent', { eventId, data }));
  }
};

/**
 * Get registrations for an event (admin only)
 */
export const getEventRegistrations = async (
  eventId: number
): Promise<ServiceResponse<EventRegistration[]>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { user } = await safeAuth.getUser();
    if (user?.error) {
      throw user.error;
    }

    const { data, error } = await safeFrom('event_registrations')
      .select('*')
      .eq('event_id', eventId)
      .order('registration_date', { ascending: false });

    if (error) {
      throw error;
    }

    return successResponse((data || []) as EventRegistration[]);
  } catch (error) {
    console.error('[eventRegistrationService] Error fetching registrations:', error);
    return errorResponse(handleServiceError(error, 'eventRegistrationService.getEventRegistrations', { eventId }));
  }
};

/**
 * Update registration status (admin only)
 */
export const updateRegistrationStatus = async (
  id: number,
  status: RegistrationStatus
): Promise<ServiceResponse<EventRegistration>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { user } = await safeAuth.getUser();
    if (user?.error) {
      throw user.error;
    }

    const { data: registration, error: updateError } = await safeFrom('event_registrations')
      .update({ status })
      .eq('id', id)
      .select()
      .single();

    if (updateError) {
      throw updateError;
    }

    const updated = registration as EventRegistration;

    // Audit log
    await auditContent.update('event_registration', id.toString(), {}, {
      status: updated.status,
    });

    return successResponse(updated);
  } catch (error) {
    console.error('[eventRegistrationService] Error updating registration status:', error);
    return errorResponse(handleServiceError(error, 'eventRegistrationService.updateRegistrationStatus', { id, status }));
  }
};

/**
 * Cancel registration (public - via email verification)
 */
export const cancelRegistration = async (
  id: number,
  email: string
): Promise<ServiceResponse<void>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    // Verify email matches registration
    const { data: registration, error: fetchError } = await safeFrom('event_registrations')
      .select('*')
      .eq('id', id)
      .eq('email', email.trim().toLowerCase())
      .maybeSingle();

    if (fetchError) {
      throw fetchError;
    }

    if (!registration) {
      return errorResponse(handleServiceError(
        new Error('Registration not found or email does not match'),
        'eventRegistrationService.cancelRegistration',
        { id, email }
      ));
    }

    // Update status to cancelled
    const { error: updateError } = await safeFrom('event_registrations')
      .update({ status: 'cancelled' as RegistrationStatus })
      .eq('id', id);

    if (updateError) {
      throw updateError;
    }

    // Audit log
    await auditContent.update('event_registration', id.toString(), {}, {
      status: 'cancelled',
    });

    return successResponse(undefined);
  } catch (error) {
    console.error('[eventRegistrationService] Error cancelling registration:', error);
    return errorResponse(handleServiceError(error, 'eventRegistrationService.cancelRegistration', { id, email }));
  }
};

/**
 * Check if email is already registered for an event
 */
export const checkRegistration = async (
  eventId: number,
  email: string
): Promise<ServiceResponse<EventRegistration | null>> => {
  if (!isSupabaseAvailable()) {
    return successResponse(null);
  }

  try {
    const { data, error } = await safeFrom('event_registrations')
      .select('*')
      .eq('event_id', eventId)
      .eq('email', email.trim().toLowerCase())
      .eq('status', 'registered')
      .maybeSingle();

    if (error) {
      throw error;
    }

    return successResponse(data as EventRegistration | null);
  } catch (error) {
    console.error('[eventRegistrationService] Error checking registration:', error);
    return errorResponse(handleServiceError(error, 'eventRegistrationService.checkRegistration', { eventId, email }));
  }
};

export const eventRegistrationService = {
  registerForEvent,
  getEventRegistrations,
  updateRegistrationStatus,
  cancelRegistration,
  checkRegistration,
};

